import React from "react";
import { Link } from "react-router-dom";
import { COLORS, navLinkStyle } from "./utils/constants";
import { translations, LanguageCode } from "./utils/translations";

// New footer palette — intentionally no purple gradient.
const FOOTER_COLORS = {
  ctaBg: "#0B1220",
  linksBg: "#111C2E",
  linksText: "#E2E8F0",
  copyrightBg: "#F8FAFC",
  copyrightText: "#64748B",
};

interface FooterProps {
  lang: LanguageCode;
}

const Footer: React.FC<FooterProps> = ({ lang }) => {
  const t = translations[lang];
  const footerLinkStyle = {
    ...navLinkStyle,
    color: FOOTER_COLORS.linksText,
    display: "block",
    marginBottom: "0.1",
    lineHeight: ".25",
    transition: "all 0.2s ease",
  };
  return (
    <footer>
      <div
        style={{
          padding: "20px 6% 20px",
          backgroundColor: FOOTER_COLORS.ctaBg,
          color: "white",
          textAlign: "center",
          marginTop: "0",
        }}
      >
        <h2
          style={{
            fontSize: "2.2rem",
            marginBottom: "0.5rem",
            marginTop: "0",
            fontWeight: 800,
          }}
        >
          {t.footer.title}
        </h2>

        <p
          style={{
            fontSize: "1.05rem",
            opacity: 0.9,
            maxWidth: "600px",
            margin: "0 auto 1.2rem",
            marginTop: "0",
          }}
        >
          {t.footer.subtitle}
        </p>

        <button
          className="footer-cta-button"
          style={{
            backgroundColor: COLORS.brandTeal,
            color: "white",
            padding: "12px 32px",
            borderRadius: "8px",
            border: "none",
            fontWeight: 700,
            fontSize: "1rem",
            cursor: "pointer",
          }}
        >
          {t.footer.button}
        </button>
      </div>

      {/* SEO FOOTER LINKS */}
      <div
        className="footer-links"
        style={{
          background: FOOTER_COLORS.linksBg,
          color: FOOTER_COLORS.linksText,
          padding: "40px 6%",
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: "2rem",
          textAlign: "left",
        }}
      >
        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.platform}</h4>
          <Link to="/social-media-manager" style={footerLinkStyle}>
            {t.footer.platform.socialMediaManager}
          </Link>
          <br />
          <Link to="/multi-account-manager" style={footerLinkStyle}>
            {t.footer.platform.multiAccountManager}
          </Link>
          <br />
          <Link to="/automation-tools" style={footerLinkStyle}>
            {t.footer.platform.automationTools}
          </Link>
          <br />
          <Link to="/analytics-dashboard" style={footerLinkStyle}>
            {t.footer.platform.analyticsDashboard}
          </Link>
        </div>

        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.services}</h4>
          <Link to="/services" style={footerLinkStyle}>
            {t.footer.services.all}
          </Link>
          <br />
          <Link to="/services/ai-development" style={footerLinkStyle}>
            {t.footer.services.aiAgents}
          </Link>
          <br />
          <Link to="/services/api-development" style={footerLinkStyle}>
            {t.footer.services.apiDevelopment}
          </Link>
          <br />
          <Link to="/services/crm-integration" style={footerLinkStyle}>
            {t.footer.services.crmIntegrations}
          </Link>
          <br />
          <Link to="/services/cloud-infrastructure" style={footerLinkStyle}>
            {t.footer.services.cloudServers}
          </Link>
        </div>

        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.useCases}</h4>
          <Link to="/multiple-instagram-accounts" style={footerLinkStyle}>
            {t.footer.useCases.instagram}
          </Link>
          <br />
          <Link to="/multiple-facebook-accounts" style={footerLinkStyle}>
            {t.footer.useCases.facebook}
          </Link>
          <br />
          <Link to="/bulk-email-campaigns" style={footerLinkStyle}>
            {t.footer.useCases.bulkEmail}
          </Link>
          <br />
          <Link to="/social-media-automation" style={footerLinkStyle}>
            {t.footer.useCases.socialAutomation}
          </Link>
        </div>

        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.resources}</h4>
          <Link to="/blog" style={footerLinkStyle}>
            {t.footer.resources.blog}
          </Link>
          <br />
          <Link to="/guides" style={footerLinkStyle}>
            {t.footer.resources.guides}
          </Link>
          <br />
          <Link to="/api-docs" style={footerLinkStyle}>
            {t.footer.resources.apiDocs}
          </Link>
          <br />
          <Link to="/tutorials" style={footerLinkStyle}>
            {t.footer.resources.tutorials}
          </Link>
        </div>

        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.freeTools}</h4>
          <Link to="/sms-calculator" style={footerLinkStyle}>
            {t.footer.freeTools.smsCalculator}
          </Link>
          <br />
          <Link to="/email-roi-calculator" style={footerLinkStyle}>
            {t.footer.freeTools.emailRoiCalculator}
          </Link>
          <br />
          <Link to="/engagement-calculator" style={footerLinkStyle}>
            {t.footer.freeTools.engagementCalculator}
          </Link>
        </div>

        <div>
          <h4 style={{ marginBottom: "12px" }}>{t.footer.columns.company}</h4>
          <Link to="/pricing" style={footerLinkStyle}>
            {t.footer.company.pricing}
          </Link>
          <br />
          <Link to="/about" style={footerLinkStyle}>
            {t.footer.company.about}
          </Link>
          <br />
          <Link to="/careers" style={footerLinkStyle}>
            {t.footer.company.careers}
          </Link>
          <br />
          <a href="/#contact" style={footerLinkStyle}>
            {t.footer.company.contact}
          </a>
        </div>
      </div>

      {/* COPYRIGHT BAR */}
      <div
        className="footer-copyright"
        style={{
          backgroundColor: FOOTER_COLORS.copyrightBg,
          color: FOOTER_COLORS.copyrightText,
          padding: "12px 8%",
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          fontSize: "0.85rem",
        }}
      >
        <span>© {new Date().getFullYear()} SocialFlow.</span>
        <span>|</span>

        <Link
          to="/privacy-policy"
          style={{ textDecoration: "underline", color: "#555" }}
        >
          {t.footer.privacyPolicy}
        </Link>

        <Link
          to="/terms-of-service"
          style={{ color: "#00B894", fontWeight: "bold" }}
        >
          {t.footer.terms}
        </Link>
      </div>
    </footer>
  );
};

export default Footer;
